import React from 'react'
import { DateTime } from 'luxon'
import { useMutation } from '@apollo/client'
import { useSnackbar } from 'notistack'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Typography from '@material-ui/core/Typography'
import DialogTitle from '@material-ui/core/DialogTitle'
import DialogContent from '@material-ui/core/DialogContent'
import EventAvailableOutlinedIcon from 'components/material-symbol/icons/EventAvailable'
import DeleteOutlinedIcon from 'components/material-symbol/icons/Delete'
import { makeStyles } from '@material-ui/core/styles'
import Dialog from 'components/dialog'
import { useDialogTitleStyles } from 'components/dialog/use-dialog-title-styles'
import { CALLOUT_FOREGROUND } from 'lib/form-field-styles'
import { REMOVE_COACH_AVAILABILITY_OVERRIDE } from './queries'

const useStyles = makeStyles(theme => ({
  bodyText: {
    fontSize: '0.875rem',
    lineHeight: 1.5,
    color: theme.palette.text.secondary,
  },
  detailsCard: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5, 2),
    backgroundColor: '#E8EFFF',
    borderRadius: 12,
  },
  detailsIcon: {
    color: '#0053FF',
  },
  detailsTextCol: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  detailsDate: {
    fontWeight: 600,
    lineHeight: 1.3,
    color: CALLOUT_FOREGROUND,
  },
  detailsTime: {
    color: theme.palette.text.secondary,
    fontVariantNumeric: 'tabular-nums',
    lineHeight: 1.3,
  },
  confirmText: {
    fontSize: '0.875rem',
    lineHeight: 1.5,
    color: CALLOUT_FOREGROUND,
    fontWeight: 500,
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(3),
  },
  cancelAction: {
    textTransform: 'none',
    fontWeight: 600,
    color: CALLOUT_FOREGROUND,
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  deleteAction: {
    textTransform: 'none',
    fontWeight: 600,
    borderRadius: 24,
    padding: theme.spacing(1, 2.5),
    minWidth: 'auto',
    boxShadow: 'none',
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.error.dark,
      boxShadow: 'none',
    },
    '& .MuiButton-startIcon': {
      marginRight: theme.spacing(0.75),
      marginLeft: 0,
    },
  },
}))

const CustomAvailabilityDestroyDialog = ({ override, onSuccess, trigger }) => {
  const classes = useStyles()
  const titleClasses = useDialogTitleStyles()
  const { enqueueSnackbar } = useSnackbar()

  const formattedDate = DateTime.fromISO(override.date).toFormat(
    'EEE, MMM d, yyyy',
  )
  const formatSlotTime = iso => {
    const dt = DateTime.fromISO(iso)
    return dt.minute === 0 ? dt.toFormat('h a') : dt.toFormat('h:mm a')
  }
  const formattedSlots = override.timeSlots
    .map(
      ts => `${formatSlotTime(ts.startTime)} – ${formatSlotTime(ts.endTime)}`,
    )
    .join(', ')

  const [removeOverride] = useMutation(REMOVE_COACH_AVAILABILITY_OVERRIDE)

  const handleRemoveOverride = closeDialog => {
    removeOverride({
      variables: { id: override.id },
      onCompleted: data => {
        if (data?.removeCoachAvailabilityOverride?.success) {
          enqueueSnackbar('One-time availability deleted', {
            variant: 'success',
          })
          onSuccess && onSuccess()
          closeDialog()
        } else {
          enqueueSnackbar(
            data?.removeCoachAvailabilityOverride?.message ||
              'Failed to delete one-time availability',
            { variant: 'error' },
          )
        }
      },
      onError: () => {
        enqueueSnackbar(
          'Failed to delete one-time availability. Please try again.',
          { variant: 'error' },
        )
      },
    })
  }

  return (
    <Dialog
      trigger={trigger}
      maxWidth="sm"
      fullWidth
      showCloseButton
      classes={{ paper: titleClasses.dialogPaper }}
    >
      {({ closeDialog }) => (
        <>
          <DialogTitle
            disableTypography
            classes={{ root: titleClasses.titleRoot }}
          >
            <Typography component="h2" className={titleClasses.title}>
              Delete one-time availability?
            </Typography>
          </DialogTitle>
          <DialogContent classes={{ root: titleClasses.content }}>
            <Box pb={1}>
              <Box mb={2}>
                <Typography className={classes.bodyText}>
                  Deleting this one-time availability will revert to your
                  general availability for this date.
                </Typography>
              </Box>

              <Box className={classes.detailsCard} mb={2}>
                <EventAvailableOutlinedIcon
                  className={classes.detailsIcon}
                  fontSize="small"
                />
                <Box className={classes.detailsTextCol}>
                  <Typography variant="body2" className={classes.detailsDate}>
                    {formattedDate}
                  </Typography>
                  <Typography variant="body2" className={classes.detailsTime}>
                    {formattedSlots}
                  </Typography>
                </Box>
              </Box>

              <Box mb={1}>
                <Typography className={classes.confirmText}>
                  Are you sure you want to delete it?
                </Typography>
              </Box>

              <Box className={classes.actions}>
                <Button className={classes.cancelAction} onClick={closeDialog}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  className={classes.deleteAction}
                  startIcon={<DeleteOutlinedIcon />}
                  onClick={() => handleRemoveOverride(closeDialog)}
                >
                  Delete
                </Button>
              </Box>
            </Box>
          </DialogContent>
        </>
      )}
    </Dialog>
  )
}

export default CustomAvailabilityDestroyDialog
